import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { AuthService } from 'services/auth.service';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { TelemetryService } from 'services/telemetry.service';
import { ScheduleParams, generateScheduleParams, ScheduleRule, Schedule_ItemType, ScheduleRuleRepeat, isRuleActiveOnDate } from '@cf-shared/data-model/schedule';
import { ScheduleRuleService, ScheduleItemDisplayInfo, RescheduleMode } from 'services/schedule-rule.service';
import { ExerciseDbService } from 'services/exercise-db.service';
import { LoggingService } from 'services/logging.service';
import { getSourceEquipments } from 'services/equipment-ui-items';
import { IEditWorkoutsPage } from 'pages/edit-workouts/edit-workouts-interface';
import { IEditNutritionPlanPage, EditNutritionPlanPageResult } from 'pages/edit-nutrition-plan/edit-nutrition-plan-interface';
import { IIntakeFormPage, IntakeStepUrl } from 'pages/intake-form/intake-form-interface';
import { IAdvancedTrainingMethodsPage } from 'pages/advanced-training-methods-page/advanced-training-methods-interface';
import { INutritionOptionsPage } from 'pages/nutrition-options-page/nutrition-options-interface';
import { IEquipmentSearchPage } from 'pages/equipment/equipment-search-interface';
import { isUsersTrainingGoalGrowth, isRecommendedTrainingGoalGrowth, DeclaredState, NutritionCoachingType, NutritionTrackingType, MacroTargetsType, usesCalorieCycling, getWorkoutsPerWeek } from '@cf-shared/data-model/user-state';
import { TrainingSplitMode, resolveTrainingSplitMode } from '@cf-shared/data-model/training-split';
import { getMinimumFrequencyForSplitMode, resolveEffectiveSplitMode } from '@cf-shared/workout-split-policy';
import {
  applyRestTimerStart,
  clampRestSeconds,
  formatRestDuration,
  getRestTimerOptions,
  REST_TIMER_DEFAULT_SECONDS,
  REST_TIMER_MAX_SECONDS,
  REST_TIMER_MIN_SECONDS,
  REST_TIMER_STEP_SECONDS,
  resolveRestTimerStart,
  RestTimerStart,
} from '@cf-shared/data-model/rest-timer';
import { AdvancedMethodsRMTesting, AdvancedMethodsMyoReps } from '@cf-shared/enums';
import { getSuggestedTrainingDedicationLevelByFrequency, getSuggestedNutritionDedicationLevel } from '@cf-shared/data-model/intake-form-state';
import { hasClientSmallScreen, hasTouchSupport } from 'environments/environment-helpers';
import { ReschedulePopover } from 'components/reschedule-popover/reschedule-popover';
import { PopoverController } from 'providers/popover-controller';
import { VirtualKeyboardService } from 'services/virtual-keyboard.service';
import { InputValue } from 'components/virtual-keyboard/virtual-keyboard';
import { NutritionPlanData, computeNutritionPlanData, computeEditNutritionPlanMacros } from 'components/cs-nutrition-plan-card/nutrition-plan-compute';
import { TrainingGoal } from '@cf-shared/enums';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { MuscleVolumeEntry } from 'services/workout-loader.service';
import { buildMuscleVolumeEntries, buildMuscleVolumeInputsAsync, formatMuscleVolumeSummary } from 'util/muscle-volume-helpers';
import { IMuscleVolumePage } from 'pages/muscle-volume-page/muscle-volume-page-interface';

import { ProgramSettingsPageParams, ProgramSettingsTab } from './program-settings-interface';
import {
  ACTIVITY_LEVEL_ROW_TEXT,
  CALORIE_CYCLING_ROW_TEXT,
  DEDICATION_LEVEL_ROW_TEXT,
  EMPTY_ROW_TEXT,
  MACRO_TARGETS_ROW_TEXT,
  MACRO_TRACKING_ROW_TEXT,
  MYO_REPS_ROW_TEXT,
  PHYSIQUE_PRIORITY_ROW_TEXT,
  RM_TESTING_ROW_TEXT,
  STRESS_LEVEL_ROW_TEXT,
  SettingsRowText,
  TRACKING_TYPE_ROW_TEXT,
  TRAINING_SPLIT_VALUE_TEXT,
  SetsPerWeekNumbers,
  readSwitchedRowText,
  readTableEntry,
  setsPerWeekNumbers,
} from './program-settings-row-text';

@Component({
    selector: 'page-program-settings',
    templateUrl: 'program-settings-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProgramSettingsPage extends CsPage<ProgramSettingsPageParams> implements AfterViewInit, OnDestroy {
  protected LL = LL;
  protected animateTabContent = false;
  goalText: string;
  recommendedGoalText: string;
  scheduleParams: ScheduleParams;
  scheduleRules: ScheduleRule[];
  usedEquipmentsNumber: number;
  equipmentsNumber: number;
  /** The saved weekly sets target and, when it differs, the number the workouts were built for (#6589). */
  protected setsPerWeek: SetsPerWeekNumbers = { target: undefined, currentPlan: undefined };

  protected nutrition: NutritionPlanData;
  protected trainingGoal: TrainingGoal;
  protected fitnessPlan: FitnessPlan;

  // Tab state
  ProgramSettingsTab = ProgramSettingsTab;
  selectedTab: ProgramSettingsTab = ProgramSettingsTab.Training;

  // Advanced section state
  isAdvancedExpanded = false;
  isNutritionAdvancedExpanded = false;

  // User settings data
  declaredState: DeclaredState;

  // Muscle volume data
  protected lowerBodyMuscles: MuscleVolumeEntry[] = [];
  protected upperBodyMuscles: MuscleVolumeEntry[] = [];
  protected muscleVolumeSummary = '';
  // #3790: subSlotIds the volume calculator could not attribute to any muscle. Passed to the
  // muscle-volume panel (so it can nudge the user to set a "Most Similar Exercise") and reflected
  // as a small hint in the summary line.
  protected uncountedSubSlotIds: string[] = [];

  // Cached training days to prevent re-rendering
  protected trainingDays: ScheduleItemDisplayInfo[] = [];

  // Cached meal times to prevent re-rendering
  protected mealTimes: ScheduleItemDisplayInfo[] = [];

  // Helper function for responsive design
  hasClientSmallScreen = hasClientSmallScreen;
  private readonly tabOrder = [ProgramSettingsTab.Training, ProgramSettingsTab.Nutrition];

  usesCalorieCycling = usesCalorieCycling;

  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    public alertCtrl: AlertController,
    protected auth: AuthService,
    protected settings: SettingsService,
    protected debug: DebugService,
    public cloudFunctionsService: CloudFunctionsService,
    private userDb: UserDb,
    private telemetry: TelemetryService,
    private exerciseDb: ExerciseDbService,
    private cd: ChangeDetectorRef,
    private popoverCtrl: PopoverController,
    private svPlanHandler: SvPlanHandlerService,
    private scheduleRuleService: ScheduleRuleService,
    private keyboardService: VirtualKeyboardService,
    private logging: LoggingService,
  ) {
    super();
  }

  // touch devices get the project's custom roller keyboard; non-touch
  // keeps the cs-select dropdown (matches cs-input-time's approach)
  isTouchDevice(): boolean {
    return hasTouchSupport();
  }

  // open the virtual keyboard in single-roller mode, one InputValue per
  // valid duration step; picks commit via setRestDefaultSecondsAsync
  handleRestDefaultRollerOpen(event: Event): void {
    if (!this.isTouchDevice()) return;
    const target = event.currentTarget as HTMLElement;
    const values = this.restTimerOptions.map(o => InputValue.fromLabel(o.seconds, o.label));
    void this.keyboardService.startKeyboard(target, () => ({
      startInRollerMode: true,
      // a rest duration reads as M:SS and the keypad has no colon key, so a
      // typed number would be ambiguous; offer the roller only (#4904)
      rollerOnly: true,
      preselectValuesNumeric1: values,
      suggestedValueNumeric1: this.restDefaultSeconds,
      currentValueNumeric1: this.restDefaultSeconds,
      rollerLabel1: LL.workouts.restTimer.rollerLabel(),
      onValueSelectedNumeric: (v: { value1: number; value2?: number }) => {
        void this.setRestDefaultSecondsAsync(Number(v.value1));
      },
    }));
  }

  ngAfterViewInit() {
    // enable tab content animation after initial render
    requestAnimationFrame(() => {
      this.animateTabContent = true;
      this.cd.markForCheck();
    });
  }

  handleSwipeForward() {
    const i = this.tabOrder.indexOf(this.selectedTab);
    if (i < this.tabOrder.length - 1) {
      this.selectedTab = this.tabOrder[i + 1];
      this.cd.markForCheck();
    }
  }

  handleSwipeBack() {
    const i = this.tabOrder.indexOf(this.selectedTab);
    if (i > 0) {
      this.selectedTab = this.tabOrder[i - 1];
      this.cd.markForCheck();
    }
  }

  async ionViewCanEnter() {
  }

  async refreshData() {
    let [fitnessPlan, declaredState, immutableState, fitnessState] = await Promise.all([
      this.userDb.loadPlanAsync(this.docWatcher),
      this.userDb.loadDeclaredStateAsync(this.docWatcher),
      this.userDb.loadImmutableStateAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
    ]);

    // store declaredState for use in template
    this.declaredState = declaredState;

    this.goalText = isUsersTrainingGoalGrowth(
      declaredState.trainingGoalGrowth,
      immutableState.gender,
      fitnessState.bfData.bf_pc,
    )
      ? LL.training.goals.muscleGrowth()
      : LL.training.goals.bodyRecomposition();

    this.recommendedGoalText = isRecommendedTrainingGoalGrowth(
      fitnessState.bfData.bf_pc,
      immutableState.gender,
      declaredState.trainingGoalGrowth,
    )
      ? LL.training.goals.muscleGrowth()
      : LL.training.goals.bodyRecomposition();

    // #6589: the row names the target the user saved, not the one the workouts were built for
    this.setsPerWeek = setsPerWeekNumbers(declaredState.trainingVolumeWeeklyAvg, fitnessPlan.trainingVolumeWeeklyAvg);

    let sourceEquipment = await getSourceEquipments(fitnessState, this.exerciseDb, this.userDb, this.settings.locale, true, undefined, this.docWatcher);
    this.usedEquipmentsNumber = sourceEquipment.filter(e => e.usedBy.length > 0).length;
    this.equipmentsNumber = sourceEquipment.length;

    // Update nutrition data
    this.fitnessPlan = fitnessPlan;
    this.trainingGoal = typeof declaredState.trainingGoalGrowth === 'boolean'
      ? (declaredState.trainingGoalGrowth ? TrainingGoal.MuscleGrowth : TrainingGoal.FatLoss)
      : (declaredState.trainingGoalGrowth || TrainingGoal.Automatic);
    this.nutrition = computeNutritionPlanData({
      fitnessPlan,
      declaredState,
      immutableState,
      fitnessState,
      scheduleRules: this.scheduleRules,
      today: this.debug.getNowLocal(),
      locale: this.settings.locale,
      reporter: this.logging
    });

    // compute muscle volume summary (include custom exercise muscle factors via similarExerciseId)
    const inputs = await buildMuscleVolumeInputsAsync(fitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
    const muscleVolume = buildMuscleVolumeEntries(fitnessPlan, declaredState, this.logging, inputs);
    this.lowerBodyMuscles = muscleVolume.lowerBodyMuscles;
    this.upperBodyMuscles = muscleVolume.upperBodyMuscles;
    this.uncountedSubSlotIds = muscleVolume.uncountedSubSlotIds;
    this.muscleVolumeSummary = this.formatVolumeSummaryWithUncounted();
  }

  // #3790: the muscle-volume summary, with a trailing "N not counted" hint when some exercises
  // could not be attributed to any muscle (custom exercises with no "Most Similar Exercise").
  private formatVolumeSummaryWithUncounted(): string {
    const base = formatMuscleVolumeSummary(this.lowerBodyMuscles, this.upperBodyMuscles);
    if (this.uncountedSubSlotIds.length === 0) return base;
    const hint = LL.training.trainingProgram.notCountedSummary({ count: this.uncountedSubSlotIds.length });
    return base ? `${base} · ${hint}` : hint;
  }

  async ngOnInit() {

    // this try block makes sure that we don't leave the spinner on forever
    try {
      let scheduleTemplate = await this.userDb.loadScheduleAsync();
      const todayISO = this.debug.getNowLocal().toISODate();

      this.scheduleRules = scheduleTemplate.rules;
      this.scheduleParams = generateScheduleParams(scheduleTemplate.rules, todayISO);
      this.updateTrainingDays();
      this.updateMealTimes();

      await this.refreshData();

    } finally {

    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    const todayISO = this.debug.getNowLocal().toISODate();

    let scheduleTemplate = await this.userDb.loadScheduleAsync();
    this.scheduleRules = scheduleTemplate.rules;
    this.scheduleParams = generateScheduleParams(scheduleTemplate.rules, todayISO);
    this.updateTrainingDays();
    this.updateMealTimes();

    await this.refreshData();
    this.cd.markForCheck();
  }

  async handleViewMuscleVolume() {
    const [fitnessPlan, declaredState, fitnessState, immutableState] = await Promise.all([
      this.userDb.loadPlanAsync(this.docWatcher),
      this.userDb.loadDeclaredStateAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
      this.userDb.loadImmutableStateAsync(this.docWatcher),
    ]);
    // recompute from fresh data each time (previous optimization may have changed volumes)
    const inputs = await buildMuscleVolumeInputsAsync(fitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
    const muscleVolume = buildMuscleVolumeEntries(fitnessPlan, declaredState, this.logging, inputs);
    this.lowerBodyMuscles = muscleVolume.lowerBodyMuscles;
    this.upperBodyMuscles = muscleVolume.upperBodyMuscles;
    this.uncountedSubSlotIds = muscleVolume.uncountedSubSlotIds;
    this.muscleVolumeSummary = this.formatVolumeSummaryWithUncounted();
    this.cd.markForCheck();

    void this.navCtrl.pushAsync(IMuscleVolumePage, {
      local: {
        lowerBodyMuscles: this.lowerBodyMuscles,
        upperBodyMuscles: this.upperBodyMuscles,
        uncountedSubSlotIds: this.uncountedSubSlotIds,
        reoptimize: {
          mode: 'save',
          fitnessPlan,
          declaredState,
          fitnessState,
          immutableState,
        },
      },
    });
  }

  handleViewEquipment() {
    void this.navCtrl.pushAsync(IEquipmentSearchPage, {});
  }

  handleEditWorkoutsButtonClick() {
    void this.navCtrl.pushAsync(IEditWorkoutsPage, {
      mode: 'edit',
      returnCallback: 'onTrainingProgramEdited'
    });
  }

  async onTrainingProgramEdited(_context: unknown, _result: unknown) {
    // no-op: training program component was removed
    this.cd.markForCheck();
  }

  handleChangeSettingsButtonClick() {
    void this.ChangePlanSettingsAsync();
  }
  async ChangePlanSettingsAsync() {
    this.telemetry.trackEvent('training_plan_change', {});
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change' });
  }

  getWorkoutSummary(): { allLetters: string; overallSets: string; overallExercises: string } {
    // TODO: if workout summary is needed, use WorkoutLoaderService.generateProgramOverviewAsync()
    return { allLetters: '', overallSets: '', overallExercises: '' };
  }

  toggleAdvanced() {
    this.isAdvancedExpanded = !this.isAdvancedExpanded;
    this.cd.markForCheck();
  }

  // rest-timer global settings: the "Start Automatically" switch + default
  // duration picker. the switch reads through resolveRestTimerStart, which
  // maps users who predate the setting from their old master switch
  // (design-docs/rest-timer.md).
  get isRestTimerAutoStart(): boolean {
    return resolveRestTimerStart(this.declaredState) === RestTimerStart.Auto;
  }

  // publicly readable (template uses it via cs-select value binding)
  get restDefaultSeconds(): number {
    return clampRestSeconds(this.declaredState?.defaultRestSeconds ?? REST_TIMER_DEFAULT_SECONDS);
  }

  get restDefaultLabel(): string {
    return formatRestDuration(this.restDefaultSeconds);
  }

  // discrete duration options driving the dropdown (shared with exercise-stats)
  readonly restTimerOptions = getRestTimerOptions();

  canDecRestDefault(): boolean {
    return this.restDefaultSeconds > REST_TIMER_MIN_SECONDS;
  }

  canIncRestDefault(): boolean {
    return this.restDefaultSeconds < REST_TIMER_MAX_SECONDS;
  }

  // save the start mode; applyRestTimerStart also writes the old master
  // switch so older app versions keep the matching behaviour
  async handleRestTimerAutoStartChange(autoStart: boolean): Promise<void> {
    if (!this.declaredState) return;
    applyRestTimerStart(this.declaredState, autoStart ? RestTimerStart.Auto : RestTimerStart.Manual);
    await this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
    this.cd.markForCheck();
  }

  private async setRestDefaultSecondsAsync(next: number): Promise<void> {
    if (!this.declaredState) return;
    this.declaredState.defaultRestSeconds = clampRestSeconds(next);
    await this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
    this.cd.markForCheck();
  }

  async handleRestDefaultStep(direction: 1 | -1): Promise<void> {
    await this.setRestDefaultSecondsAsync(this.restDefaultSeconds + direction * REST_TIMER_STEP_SECONDS);
  }

  async handleRestDefaultSelect(value: number): Promise<void> {
    await this.setRestDefaultSecondsAsync(Number(value));
  }

  toggleNutritionAdvanced() {
    this.isNutritionAdvancedExpanded = !this.isNutritionAdvancedExpanded;
    this.cd.markForCheck();
  }

  // Helper methods for Advanced Training Methods display.
  // The text each of these rows shows lives in program-settings-row-text.ts, one
  // table per kind of choice, so a new option cannot be added without its text.
  private myoRepsRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readTableEntry(MYO_REPS_ROW_TEXT, this.declaredState.advancedMethodsMyoReps, EMPTY_ROW_TEXT);
  }

  getMyoRepsValue(): TranslatedString {
    return this.myoRepsRowText().value();
  }

  getMyoRepsDescription(): TranslatedString {
    return this.myoRepsRowText().note();
  }

  isMyoRepsEnabled(): boolean {
    return this.declaredState?.advancedMethodsMyoReps !== AdvancedMethodsMyoReps.Avoid;
  }

  formatSleepHours(): string {
    const hours = this.declaredState?.sleepHours;
    if (hours === undefined) return '?';
    const rounded = Math.round(hours * 2) / 2;
    const isExact = hours === rounded;
    return isExact ? `${rounded}` : `~${rounded}`;
  }

  private rmTestingRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readTableEntry(RM_TESTING_ROW_TEXT, this.declaredState.advancedMethodsRMTesting, EMPTY_ROW_TEXT);
  }

  getRMTestingValue(): TranslatedString {
    return this.rmTestingRowText().value();
  }

  getRMTestingDescription(): TranslatedString {
    return this.rmTestingRowText().note();
  }

  isRMTestingEnabled(): boolean {
    return this.declaredState?.advancedMethodsRMTesting !== AdvancedMethodsRMTesting.Avoid;
  }

  private calorieCyclingRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readSwitchedRowText(CALORIE_CYCLING_ROW_TEXT, usesCalorieCycling(this.declaredState));
  }

  getCalorieCyclingValue(): TranslatedString {
    return this.calorieCyclingRowText().value();
  }

  getCalorieCyclingDescription(): TranslatedString {
    return this.calorieCyclingRowText().note();
  }

  private activityLevelRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readTableEntry(ACTIVITY_LEVEL_ROW_TEXT, this.declaredState.activityLevel, EMPTY_ROW_TEXT);
  }

  getActivityLevelValue(): TranslatedString {
    return this.activityLevelRowText().value();
  }

  getActivityLevelDescription(): TranslatedString {
    return this.activityLevelRowText().note();
  }

  private stressLevelRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readTableEntry(STRESS_LEVEL_ROW_TEXT, this.declaredState.stressLevel, EMPTY_ROW_TEXT);
  }

  getStressLevelValue(): TranslatedString {
    return this.stressLevelRowText().value();
  }

  getStressLevelDescription(): TranslatedString {
    return this.stressLevelRowText().note();
  }

  private physiquePriorityRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readTableEntry(PHYSIQUE_PRIORITY_ROW_TEXT, this.declaredState.muscleGrowthPreferenceMain, EMPTY_ROW_TEXT);
  }

  getPhysiquePriorityValue(): TranslatedString {
    return this.physiquePriorityRowText().value();
  }

  getPhysiquePriorityCustomText(): TranslatedString {
    if (!this.declaredState) return untranslatedString('');
    // If Custom is selected, return list of customized muscle groups
    // For now, return empty string - this would need to be implemented based on muscleGrowthPreferenceTable
    return untranslatedString('');
  }

  getPhysiquePriorityDescription(): TranslatedString {
    return this.physiquePriorityRowText().note();
  }

  private setsPerWeekRowText(): SettingsRowText {
    if (!this.declaredState?.trainingDedicationLevel) return EMPTY_ROW_TEXT;
    return readTableEntry(DEDICATION_LEVEL_ROW_TEXT, this.declaredState.trainingDedicationLevel, EMPTY_ROW_TEXT);
  }

  getSetsPerWeekValue(): TranslatedString {
    return this.setsPerWeekRowText().value();
  }

  getSetsPerWeekCustomText(): TranslatedString {
    if (!this.setsPerWeek.target) return untranslatedString('');
    return LL.editTrainingProgram.labels.sets({ count: this.setsPerWeek.target });
  }

  getSetsPerWeekDescription(): TranslatedString {
    return this.setsPerWeekRowText().note();
  }

  handleUpdateProgram() {
    void this.navCtrl.pushAsync(IAdvancedTrainingMethodsPage, {});
  }

  handleUpdateNutritionOptions() {
    void this.navCtrl.pushAsync(INutritionOptionsPage, {});
  }

  handleUpdateSchedule() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.Schedule });
  }

  handleUpdateLifestyle() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.Lifestyle });
  }

  handleUpdatePriority() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.Goals });
  }

  handleUpdateVolume() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.TrainingVolume });
  }

  // ---- workout split (#4591).

  /**
   * The name of the workout split the user is on. Unset means Optimal.
   *
   * `resolveTrainingSplitMode` already turns a missing or unrecognised stored
   * value into Optimal, so the Optimal entry in the table is what an unset split
   * reads as. The fallback below can only be reached if that stops being true.
   */
  getTrainingSplitValue(): TranslatedString {
    const readName = readTableEntry(
      TRAINING_SPLIT_VALUE_TEXT,
      resolveTrainingSplitMode(this.declaredState),
      TRAINING_SPLIT_VALUE_TEXT[TrainingSplitMode.Optimal],
    );
    return readName();
  }

  /**
   * The line under it, which is empty unless something needs saying.
   *
   * The one case that does: the user has since dropped below the training days
   * their chosen split needs. Their choice is kept, but the program is built as
   * Optimal until they train often enough again, and this is what tells them so.
   *
   * There is deliberately no description of the split itself here, even though
   * the intake form's chooser does show one under every option. This row states
   * which split the user is already on; the descriptions exist to help somebody
   * DECIDE, which happens on the chooser. Repeating one here would put a
   * sentence under a settings row whose answer the user has already given.
   */
  getTrainingSplitDescription(): TranslatedString {
    const stored = resolveTrainingSplitMode(this.declaredState);
    const effective = resolveEffectiveSplitMode(stored, getWorkoutsPerWeek(this.declaredState));
    if (effective !== stored) {
      return LL.intake.trainingSplit.availableAtFrequency({ minimum: getMinimumFrequencyForSplitMode(stored) });
    }
    return untranslatedString('');
  }

  /**
   * Opens the intake form at the step the choice lives on — the same thing the
   * two rows above this one do. Changing it there goes through the form's own
   * "your program needs rebuilding" flow, which asks before rebuilding and then
   * re-solves and saves the workouts properly.
   */
  handleUpdateTrainingSplit() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.TrainingVolume });
  }

  getWorkoutsPerWeek(): number {
    if (!this.scheduleRules) return 0;
    const todayISO = this.debug.getNowLocal().toISODate();
    const workoutRules = this.scheduleRules.filter(
      rule => rule.itemType === Schedule_ItemType.Workout &&
              rule.repeat === ScheduleRuleRepeat.Weekly &&
              isRuleActiveOnDate(rule, todayISO)
    );
    return workoutRules.length;
  }

  private updateTrainingDays() {
    if (!this.scheduleRules) {
      this.trainingDays = [];
      return;
    }
    const todayISO = this.debug.getNowLocal().toISODate();
    this.trainingDays = this.scheduleRuleService.getActiveTrainingDays(this.scheduleRules, todayISO);
  }

  private updateMealTimes() {
    if (!this.scheduleRules) {
      this.mealTimes = [];
      return;
    }
    const todayISO = this.debug.getNowLocal().toISODate();
    this.mealTimes = this.scheduleRuleService.getActiveMealTimes(this.scheduleRules, todayISO);
  }

  // sync declaredState.trainingDays with schedule rules after reschedule
  private async syncDeclaredStateTrainingDays(rules: ScheduleRule[]) {
    const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    const todayISO = this.debug.getNowLocal().toISODate();
    const trainingDays = this.scheduleRuleService.getTrainingDaysFromRules(rules, todayISO);
    let modified = false;

    for (let i = 0; i < trainingDays.length; i++) {
      if (declaredState.trainingDays[i] !== trainingDays[i]) {
        declaredState.trainingDays[i] = trainingDays[i];
        modified = true;
      }
    }

    if (modified) {
      await this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
    }
  }

  async handleRescheduleWorkout(workout: ScheduleItemDisplayInfo, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    const result = await ReschedulePopover.present(
      this.popoverCtrl,
      event.target as HTMLElement,
      {
        mode: RescheduleMode.ScheduleEditing,
        displayInfo: workout,
      }
    );

    if (result?.success) {
      // reload schedule data to show changes
      const todayISO = this.debug.getNowLocal().toISODate();
      let scheduleTemplate = await this.userDb.loadScheduleAsync();
      this.scheduleRules = scheduleTemplate.rules;
      this.scheduleParams = generateScheduleParams(scheduleTemplate.rules, todayISO);
      this.updateTrainingDays();
      this.updateMealTimes();
      await this.syncDeclaredStateTrainingDays(scheduleTemplate.rules);
      this.cd.markForCheck();
    }
  }

  async handleRescheduleMeal(meal: ScheduleItemDisplayInfo, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    const result = await ReschedulePopover.present(
      this.popoverCtrl,
      event.target as HTMLElement,
      {
        mode: RescheduleMode.ScheduleEditing,
        displayInfo: meal,
      }
    );

    if (result?.success) {
      // reload schedule data to show changes
      const todayISO = this.debug.getNowLocal().toISODate();
      let scheduleTemplate = await this.userDb.loadScheduleAsync();
      this.scheduleRules = scheduleTemplate.rules;
      this.scheduleParams = generateScheduleParams(scheduleTemplate.rules, todayISO);
      this.updateTrainingDays();
      this.updateMealTimes();
      this.cd.markForCheck();
    }
  }

  async handleEditNutritionPlan() {
    const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
    const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);

    const isFlexible = declaredState.macroTargets === MacroTargetsType.Flexible;
    const today = this.debug.getNowLocal();
    const plan = this.fitnessPlan;
    const overrides = plan.overrideMacros;

    // the editor needs both the user's current targets and the recommendation to open safely — it
    // decides what counts as one of their own overrides by comparing the two, and it saves what it
    // decides. Missing either means we do not open it at all (df-f3070b96b839).
    const macros = computeEditNutritionPlanMacros(
      plan, declaredState, immutableState, fitnessState, this.scheduleRules, today, isFlexible, this.logging);
    if (macros === null) {
      await this.alertCtrl.showAlertAsync({
        message: LL.editNutritionPlan.couldNotComputeTargets(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }
    const { current, recommended } = macros;

    void this.navCtrl.pushAsync(IEditNutritionPlanPage, {
      returnCallback: 'onNutritionPlanEdited',
      local: {
        isFlexible,
        gender: immutableState.gender,
        mealsDaily: declaredState.mealFrequency,
        dailyIntake: plan.overrideDailyAverageIntake ?? plan.actualDailyAverageIntake ?? 0,
        protein: current.protein,
        carbs: current.carbs,
        fat: current.fat,
        fiber: current.fiber,
        recommendedDailyIntake: recommended.dailyIntake,
        recommendedProtein: recommended.protein,
        recommendedCarbs: recommended.carbs,
        recommendedFat: recommended.fat,
        recommendedFiber: recommended.fiber,
        carbFatRatio: overrides?.carbFatRatio,
        autoAdjust: plan.overrideMacros?.autoAdjust,
      },
    });
  }

  /**
   * Callback invoked by NavController when returning from EditNutritionPlanPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onNutritionPlanEdited(_context: unknown, result: EditNutritionPlanPageResult | undefined) {
    // if user cancelled, do nothing
    if (result === undefined) {
      return;
    }

    // apply calorie override and macro overrides
    this.fitnessPlan.overrideDailyAverageIntake = result.dailyIntake;
    this.fitnessPlan.overrideMacros = result.overrideMacros;

    const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    const fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
    await this.svPlanHandler.updatePlanAsync(this.fitnessPlan, declaredState, fitnessState, immutableState);

    await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    await this.userDb.clearBacklogCacheAsync();

    await this.refreshData();
    this.cd.markForCheck();
  }

  // Nutrition-specific helper methods
  private macroTargetsRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readSwitchedRowText(MACRO_TARGETS_ROW_TEXT, this.declaredState.nutritionCoaching !== NutritionCoachingType.None);
  }

  getMacroTargetsValue(): TranslatedString {
    return this.macroTargetsRowText().value();
  }

  getMacroTargetsDescription(): TranslatedString {
    return this.macroTargetsRowText().note();
  }

  private macroTrackingRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readSwitchedRowText(MACRO_TRACKING_ROW_TEXT, this.declaredState.nutritionTracking !== NutritionTrackingType.None);
  }

  getMacroTrackingValue(): TranslatedString {
    return this.macroTrackingRowText().value();
  }

  getMacroTrackingDescription(): TranslatedString {
    return this.macroTrackingRowText().note();
  }

  private trackingTypeRowText(): SettingsRowText {
    if (!this.declaredState) return EMPTY_ROW_TEXT;
    return readSwitchedRowText(TRACKING_TYPE_ROW_TEXT, this.declaredState.macroTargets === MacroTargetsType.Flexible);
  }

  getTrackingTypeValue(): TranslatedString {
    return this.trackingTypeRowText().value();
  }

  getTrackingTypeDescription(): TranslatedString {
    return this.trackingTypeRowText().note();
  }

  isMacroTargetsRecommended(): boolean {
    return this.declaredState?.nutritionCoaching !== NutritionCoachingType.None;
  }

  isMacroTrackingRecommended(): boolean {
    return this.declaredState?.nutritionTracking !== NutritionTrackingType.None;
  }

  isTrackingTypeRecommended(): boolean {
    return this.declaredState?.macroTargets === MacroTargetsType.Flexible;
  }

  isSetsPerWeekRecommended(): boolean {
    if (!this.declaredState?.trainingDedicationLevel) return false;
    const suggested = getSuggestedTrainingDedicationLevelByFrequency(this.trainingDays.length);
    return this.declaredState.trainingDedicationLevel === suggested;
  }

  isEnergyBalanceRecommended(): boolean {
    return this.declaredState?.nutritionDedicationLevel === getSuggestedNutritionDedicationLevel();
  }

  getMealFrequency(): TranslatedString {
    if (!this.declaredState) return untranslatedString('');
    const mealCount = this.declaredState.mealFrequency || 4;
    return LL.programSettings.nutrition.mealsPerDay({ count: mealCount });
  }

  getGoalDescription(): TranslatedString {
    // can add description text if needed
    return untranslatedString('');
  }

  getEnergyBalanceLabel(): TranslatedString {
    if (!this.declaredState || this.declaredState.intakeDeltaPercent === undefined) return untranslatedString('');
    const percent = this.declaredState.intakeDeltaPercent;
    return percent >= 0
      ? LL.programSettings.nutrition.calorieSurplus()
      : LL.programSettings.nutrition.calorieDeficit();
  }

  private energyBalanceRowText(): SettingsRowText {
    if (!this.declaredState?.nutritionDedicationLevel) return EMPTY_ROW_TEXT;
    return readTableEntry(DEDICATION_LEVEL_ROW_TEXT, this.declaredState.nutritionDedicationLevel, EMPTY_ROW_TEXT);
  }

  getEnergyBalanceValue(): TranslatedString {
    return this.energyBalanceRowText().value();
  }

  getEnergyBalanceDescription(): TranslatedString {
    return this.energyBalanceRowText().note();
  }

  getEnergyBalanceCustomText(): TranslatedString {
    if (!this.fitnessPlan?.actualDailyAverageIntake || this.declaredState?.intakeDeltaPercent === undefined) return untranslatedString('');

    const percent = this.declaredState.intakeDeltaPercent;
    // calculate the calorie adjustment from maintenance
    // dailyIntake = maintenance * (1 + delta/100)
    // adjustment = maintenance * delta/100 = dailyIntake * delta/(100 + delta)
    const adjustment = this.fitnessPlan.actualDailyAverageIntake * percent / (100 + percent);

    const sign = percent >= 0 ? '+' : '';
    return LL.programSettings.nutrition.energyBalanceCustomText({
      sign,
      percent: percent.toFixed(0),
      adjustment: adjustment.toFixed(0),
    });
  }

  handleUpdateGoal() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.Goals });
  }

  handleUpdateNutritionSchedule() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.Schedule });
  }

  handleUpdateEnergyBalance() {
    void this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.EnergyBalance });
  }

}